// Teamverwaltung (design_handoff_team): reine Hilfen für Liste, Detailseite und Dialoge.
// Fachlich unverändert aus TeamListe / TeamMitarbeiterDetailsModal herausgelöst.
import { alsModulListe } from '../sidebar/navigation';

export interface TeamMitglied {
  id: string;
  full_name: string;
  email: string;
  role: string;
  status: string | null;
  avatar_url?: string | null;
  created_at: string;
  allowed_modules?: unknown;
  ist_angestellt?: boolean | null;
  gps_beim_stempeln?: boolean | null;
}

export interface PortalModul { id: string; name: string; is_active: boolean }

/** Initialen wie bisher: Anfangsbuchstaben aller Namensteile, höchstens zwei; ohne Namen „??“. */
export function initialen(name: string | null | undefined): string {
  if (!name) return '??';
  return name.split(' ').map(t => t[0] ?? '').join('').toUpperCase().substring(0, 2) || '??';
}

/** Rolle in der Tabelle (lang) bzw. auf Karten (kurz) – nur „admin“ wird unterschieden, wie im Code. */
export function rollenLabel(role: string, lang: boolean): string {
  if (role === 'admin') return lang ? 'Administrator' : 'Admin';
  return lang ? 'Mitarbeiter' : 'Team';
}

/** Rolle im Kopf der Detailseite. */
export function rollenName(role: string | null | undefined): string {
  const namen: Record<string, string> = { admin: 'Admin', team: 'Team', hausmeister: 'Hausmeister', eigentuemer: 'Eigentümer', mieter: 'Mieter', dienstleister: 'Dienstleister' };
  return (role && namen[role]) || role || 'Mitarbeiter';
}

/** Anzeige-Status: leer = „Aktiv“. */
export const statusText = (status: string | null | undefined): string => status || 'Aktiv';
export const istAktiv = (status: string | null | undefined): boolean => statusText(status).toLowerCase() === 'aktiv';

/** Kurz-ID der Karten (#B72E44). */
export const kurzId = (id: string): string => id.slice(0, 6).toUpperCase();

/** Suche nach Name oder E-Mail (Groß-/Kleinschreibung egal). */
export function passtSuche(m: Pick<TeamMitglied, 'full_name' | 'email'>, suche: string): boolean {
  const s = suche.toLowerCase();
  return (m.full_name || '').toLowerCase().includes(s) || (m.email || '').toLowerCase().includes(s);
}

/** Zähler unter der Suche. */
export function zaehlerText(anzahl: number, gefiltert: boolean): string {
  return `${anzahl} ${anzahl === 1 ? 'Teammitglied' : 'Teammitglieder'}${gefiltert ? ' im Filter' : ''}`;
}

/** Module beim Öffnen der Berechtigungen: leere Liste ⇒ Standard ['dashboard']. */
export function startModule(allowed: unknown): string[] {
  const liste = alsModulListe(allowed);
  return liste.length > 0 ? liste : ['dashboard'];
}

/** Text im Widget „Zugriffsrechte“ – Modulnamen, sonst IDs, ohne Freigaben der Dashboard-Hinweis. */
export function rechteText(allowed: unknown, module: readonly Pick<PortalModul, 'id' | 'name'>[]): string {
  const ids = alsModulListe(allowed);
  if (ids.length === 0) return 'Dieses Mitglied hat standardmäßig Zugriff auf das Dashboard.';
  const namen = ids.map(id => module.find(m => m.id === id)?.name).filter((n): n is string => !!n);
  return `Freigeschaltete Bereiche: ${(namen.length > 0 ? namen : ids).join(', ')}`;
}

/** Avatar-Farbe aus der ID (nur Tokens). */
const AVATAR_TOENE = ['--info', '--warn', '--ok', '--pur', '--bad', '--acc'] as const;
export function avatarHintergrund(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const t = AVATAR_TOENE[h % AVATAR_TOENE.length];
  return `linear-gradient(160deg, color-mix(in srgb, var(${t}) 70%, #fff), var(${t}))`;
}
